// IEA Site Survey, step 4: Collect (pass 2, doors open). Pure: data in, answers out.
// Words: IEA_STEP4_COLLECT_WORDS.md (approved 2026-10-16). Mockup: MOCKUP_IEA_COLLECT.html v2 (approved).
// Field list: IEA_1584_FIELD_REVIEW.md rev 2, amended by IEA_SURVEY_DESIGN.md section 7.
// Unit tested: tests/unit/survey-collect.test.ts.
//
// Saves: one IEA Data record per asset (ID = Asset ID), wire fields on the Map's Connections row.
// Figured, never stored: Done, the rings, the work list, the totals.

import { typicalFor, type PackAsset } from "./survey";
import { codeOf, isPositionPoint, placeOf, shortName, type MapLink, type MapNode, type MapTree, type Registry } from "./survey-map";

// ---- Lists (the Lists page holds them; these are the approved starting lists) ----

export const LIST_NAMES = {
  size: "Conductor Size",
  sets: "Conductors per Phase",
  material: "Conductor Material",
  insulation: "Insulation Type",
  raceway: "Raceway Type",
  starter: "Starter Type",
  reason: "Not Opened Reason",
} as const;

export const DEFAULT_LISTS: Record<string, string[]> = {
  "Conductor Size": ["12", "10", "8", "6", "4", "3", "2", "1", "1/0", "2/0", "3/0", "4/0", "250", "300", "350", "400", "500", "14", "600", "750", "Other"],
  "Conductors per Phase": ["1", "2", "3", "4", "5", "6", "8"],
  "Conductor Material": ["Copper", "Aluminum", "Other"],
  "Insulation Type": ["THHN/THWN-2", "XHHW-2", "THHN/THWN", "XHHW", "RHW-2", "MC Cable", "MC-HCF (Hospital)", "Other"],
  "Raceway Type": ["EMT", "Rigid Steel (RMC/IMC)", "Rigid Aluminum", "PVC", "Flex (FMC/LFMC)", "Cable Tray", "Other"],
  "Starter Type": ["FVNR", "FVR", "Two-Speed", "Reduced Voltage Autotransformer", "Wye-Delta", "Part Winding", "Soft Starter", "VFD", "Contactor Only", "Feeder (No Starter)", "Other"],
  "Not Opened Reason": ["Locked", "Load Can't Go Off", "Not Safe", "Other"],
  // Robert's list, 2026-10-16. MCCs, switchboards, panels and switchgear. Non-Fused Disconnect Switch and
  // None carried over from the first list: a disconnect with no fuses, and a position with no device.
  "Device Type": [
    "Motor Circuit Protector (MCP) / Magnetic-Only Breaker",
    "Thermal-Magnetic Molded Case Circuit Breaker (MCCB)",
    "Electronic Trip Molded Case Circuit Breaker (MCCB)",
    "Insulated Case Circuit Breaker (ICCB)",
    "Low-Voltage Power Circuit Breaker (LVPCB)",
    "Medium-Voltage Vacuum Circuit Breaker",
    "Medium-Voltage Air-Magnetic Circuit Breaker",
    "Molded Case Switch (Non-Automatic)",
    "Fusible Disconnect / Switch (Cartridge Fuses)",
    "Non-Fused Disconnect Switch",
    "Panelboard Branch Circuit Breaker (Bolt-on / Plug-in)",
    "Medium-Voltage Distribution Fuse (E-Rated / R-Rated)",
    "Low-Voltage Cartridge Fuse (Class L, J, RK1, RK5, CC)",
    "None",
  ],
  "Fuse Class": ["L", "J", "RK1", "RK5", "CC", "T", "E-Rated", "R-Rated", "Other"],
  "Trip Type": ["Thermal-Magnetic", "Electronic", "Solid-State", "Magnetic Only"],
  "Winding Connection": ["Delta-Wye", "Delta-Delta", "Wye-Wye", "Wye-Delta"],
  // A two-can pole bank is usually an open delta (Robert 2026-10-19).
  "Winding Connection Open Delta": ["Open Delta", "Delta-Wye", "Delta-Delta", "Wye-Wye", "Wye-Delta"],
  Grounding: ["Solidly Grounded", "Resistance Grounded", "Ungrounded", "Not Sure"],
  "Utility Transformer Type": ["Pad Mount", "Pole Mount"],
  "Transformers on Pole": ["1", "2", "3"],
  "Electrode Configuration": ["VCB", "VCBB", "HCB", "VOA", "HOA"],
  "Main or MLO": ["Main", "MLO"],
  "Main In Own Compartment": ["Yes", "No", "Not Sure"],
  "Built-in Arc Protection": ["None", "Arc-Resistant", "Active Mitigation", "Line-Side Isolation"],
  Transition: ["Open", "Closed", "Delayed"],
  "Fixed or Switched": ["Fixed", "Switched"],
  "Yes or No": ["Yes", "No"],
  "In or Out": ["In", "Out"],
  "Maintenance Switch": ["None", "Present"],
  "Zone Interlock": ["Not Equipped", "On", "Off"],
};

export interface ListValue {
  listName: string;
  value: string;
  order: number | null;
  isActive: boolean;
}

/**
 * The dropdown for a list: the approved list, plus any value added on the Lists page, before Other.
 * A value switched off on the Lists page drops out.
 */
export function listFor(name: string, lists: readonly ListValue[]): string[] {
  const base = DEFAULT_LISTS[name] ?? [];
  const page = lists.filter((v) => v.listName === name).sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9));
  const off = new Set(page.filter((v) => !v.isActive).map((v) => v.value));
  const out = base.filter((v) => !off.has(v));
  const extra = page.filter((v) => v.isActive && !out.includes(v.value)).map((v) => v.value);
  const other = out.indexOf("Other");
  if (other < 0) return [...out, ...extra];
  return [...out.slice(0, other), ...extra, ...out.slice(other)];
}

// ---- Cards ----

export type CardKind = "housing" | "xfmr" | "utl" | "gen" | "ats" | "lr" | "cap" | "box";

export interface Field {
  name: string;
  /** A list name: the field is a dropdown. */
  list?: string;
  /** Required for Done. */
  req?: boolean;
  /** From the office (utility letter, datasheet). Never amber, never counts against Done. */
  office?: boolean;
  /** Typed as a number. */
  num?: boolean;
  /** Shown only when this answers true. */
  when?: (r: Rec) => boolean;
  /** Always known at the gear (what kind of transformer, how many cans): no Not Visible. */
  known?: boolean;
}

export interface Card {
  kind: CardKind;
  title: string;
  photos: string[];
  optPhotos: string[];
  fields: Field[];
  /** The card asks Opened or Not Opened. */
  opened: boolean;
}

const HOUSINGS = new Set(["MCC", "PNL", "SWBD", "SWGR", "LP", "DP", "PP", "MDP", "MSB", "SB", "LC"]);

export function cardKindOf(a: Pick<PackAsset, "assetId" | "equipmentCode" | "equipmentType">): CardKind {
  const c = codeOf(a);
  if (c === "UTL" || /utility service/i.test(a.equipmentType)) return "utl";
  if (c === "GEN" || /generator/i.test(a.equipmentType)) return "gen";
  if (c === "XFMR" || c === "XFR" || /transformer/i.test(a.equipmentType)) return "xfmr";
  if (c === "ATS" || c === "MTS" || /transfer switch/i.test(a.equipmentType)) return "ats";
  if (c === "LR" || /line reactor/i.test(a.equipmentType)) return "lr";
  if (c === "CAP" || c === "PFC" || /capacitor/i.test(a.equipmentType)) return "cap";
  if (HOUSINGS.has(c)) return "housing";
  return "box";
}

/** What a Device Type is, for the fields it asks. Reads Robert's list (2026-10-16) and the first list's
 *  words, so a value saved before the change still asks the right fields. */
export type DeviceKind = "breaker" | "switch" | "fusedSwitch" | "fuse" | "none" | "";
export function deviceKind(type: string): DeviceKind {
  const t = type.trim();
  if (!t) return "";
  if (t === "None") return "none";
  if (/Fusible|Fused Switch/i.test(t) && !/Non-Fused/i.test(t)) return "fusedSwitch";
  if (/Fuse\b|Fuse \(/i.test(t) || t === "Fuse") return "fuse";
  if (/Breaker|MCP/i.test(t)) return "breaker";
  if (/Switch/i.test(t)) return "switch";
  return "";
}

/** The trip the Device Type already names, so Trip Type is not asked twice. Empty when it varies. */
export function impliedTrip(type: string): string {
  if (/MCP|Magnetic-Only/i.test(type)) return "Magnetic Only";
  if (/Thermal-Mag|Panelboard Branch/i.test(type)) return "Thermal-Magnetic";
  if (/Electronic/i.test(type)) return "Electronic";
  return "";
}

const devType = (prefix: string, r: Rec) => String(r.f[`${prefix}Device Type`] ?? "");
const tripOf = (prefix: string, r: Rec) => impliedTrip(devType(prefix, r)) || String(r.f[`${prefix}Trip Type`] ?? "");
const isElectronic = (prefix: string) => (r: Rec) => /Electronic|Solid-State/.test(tripOf(prefix, r));
const isThermalMag = (prefix: string) => (r: Rec) => tripOf(prefix, r) === "Thermal-Magnetic";
const isMagOnly = (prefix: string) => (r: Rec) => tripOf(prefix, r) === "Magnetic Only";
const hasBreaker = (prefix: string) => (r: Rec) => deviceKind(devType(prefix, r)) === "breaker";
const asksTrip = (prefix: string) => (r: Rec) => hasBreaker(prefix)(r) && !impliedTrip(devType(prefix, r));
const hasFuse = (prefix: string) => (r: Rec) => ["fusedSwitch", "fuse"].includes(deviceKind(devType(prefix, r)));
const isMain = (r: Rec) => r.f["Main or MLO"] === "Main";
const isPole = (r: Rec) => r.f["Transformer Type"] === "Pole Mount";
const isOpenBank = (r: Rec) => isPole(r) && String(r.f["Transformers on Pole"] ?? "") === "2";
const both = (a: (r: Rec) => boolean, b: (r: Rec) => boolean) => (r: Rec) => a(r) && b(r);

/** The breaker and fuse fields, under a prefix ("" for a row or a box, "Main " for a housing main). */
export function deviceFields(prefix: string, gate: (r: Rec) => boolean = () => true): Field[] {
  const p = prefix;
  const g = (extra: (r: Rec) => boolean) => both(gate, extra);
  return [
    { name: `${p}Device Type`, list: "Device Type", req: true, when: gate },
    { name: `${p}Manufacturer`, when: gate },
    { name: `${p}Model`, when: g(hasBreaker(p)) },
    { name: `${p}Catalog No.`, when: g(hasBreaker(p)) },
    { name: `${p}Interrupting Rating (kA)`, num: true, when: g(hasBreaker(p)) },
    { name: `${p}Frame Amps`, num: true, req: true, when: g(hasBreaker(p)) },
    // Trip Type is asked only when the Device Type does not already say it (ICCB, LVPCB, medium voltage).
    { name: `${p}Trip Type`, list: "Trip Type", req: true, when: g(asksTrip(p)) },
    { name: `${p}Trip Amps`, num: true, when: g(isThermalMag(p)) },
    { name: `${p}INST Setting`, when: g((r) => isThermalMag(p)(r) || isMagOnly(p)(r)) },
    { name: `${p}Sensor Rating`, num: true, when: g(isElectronic(p)) },
    { name: `${p}Rating Plug`, num: true, when: g(isElectronic(p)) },
    { name: `${p}Trip Unit Model`, when: g(isElectronic(p)) },
    { name: `${p}LTPU`, when: g(isElectronic(p)) },
    { name: `${p}LTD`, when: g(isElectronic(p)) },
    { name: `${p}STPU`, when: g(isElectronic(p)) },
    { name: `${p}ST Delay`, when: g(isElectronic(p)) },
    { name: `${p}INST`, when: g(isElectronic(p)) },
    { name: `${p}I²t`, list: "In or Out", when: g(isElectronic(p)) },
    { name: `${p}GF Setting`, when: g(isElectronic(p)) },
    { name: `${p}GF Delay`, when: g(isElectronic(p)) },
    { name: `${p}Maintenance Switch`, list: "Maintenance Switch", when: g(isElectronic(p)) },
    { name: `${p}Zone Interlock`, list: "Zone Interlock", when: g(isElectronic(p)) },
    { name: `${p}Opening Time (cycles)`, num: true, when: g(hasBreaker(p)) },
    { name: `${p}Fuse Manufacturer`, when: g(hasFuse(p)) },
    { name: `${p}Fuse Part No.`, when: g(hasFuse(p)) },
    { name: `${p}Fuse Amp Rating`, num: true, req: true, when: g(hasFuse(p)) },
    { name: `${p}Fuse Class`, list: "Fuse Class", when: g(hasFuse(p)) },
    { name: `${p}Current-Limiting`, list: "Yes or No", when: g(hasFuse(p)) },
    { name: `${p}All Three Fuses Match`, list: "Yes or No", when: g(hasFuse(p)) },
  ];
}

const HOUSING_FIELDS: Field[] = [
  { name: "Manufacturer", req: true },
  { name: "Model", req: true },
  { name: "Shop Order / Serial No." },
  { name: "Housing Ampere Rating", num: true, req: true },
  { name: "Equipment SCCR (kA)", num: true, req: true },
  { name: "Equipment WCR (kA)", num: true },
  { name: "Height (in)", num: true },
  { name: "Width (in)", num: true },
  { name: "Depth (in)", num: true },
  { name: "Electrode Configuration", list: "Electrode Configuration", req: true },
  { name: "Working Distance (in)", num: true },
  { name: "Built-in Arc Protection", list: "Built-in Arc Protection" },
  { name: "Main or MLO", list: "Main or MLO", req: true },
  { name: "Main In Own Compartment", list: "Main In Own Compartment", req: true, when: isMain },
  ...deviceFields("Main ", isMain),
];

const BOX_FIELDS: Field[] = [
  { name: "Manufacturer" },
  { name: "Housing Ampere Rating", num: true },
  { name: "Electrode Configuration", list: "Electrode Configuration" },
  ...deviceFields(""),
];

export const CARDS: Record<CardKind, Card> = {
  housing: { kind: "housing", title: "Housing", photos: ["Nameplate", "Front", "Old Label"], optPhotos: [], fields: HOUSING_FIELDS, opened: true },
  box: { kind: "box", title: "Enclosure", photos: ["Nameplate", "Front"], optPhotos: ["Old Label"], fields: BOX_FIELDS, opened: true },
  xfmr: {
    kind: "xfmr",
    title: "Transformer",
    photos: ["Nameplate", "Front"],
    optPhotos: [],
    fields: [
      { name: "kVA", num: true, req: true },
      { name: "Primary V", num: true, req: true },
      { name: "Secondary V", num: true, req: true },
      { name: "%Z", num: true, req: true },
      { name: "Winding Connection", list: "Winding Connection", req: true },
      { name: "Grounding", list: "Grounding", req: true },
      { name: "Tap Setting" },
      { name: "Serial No." },
      { name: "X/R", num: true },
    ],
    opened: false,
  },
  utl: {
    kind: "utl",
    title: "Utility Service",
    photos: ["Pad Mount Plate"],
    optPhotos: ["Meter", "Service Entrance"],
    // Robert 2026-10-19: a utility transformer is a pad mount or a pole mount; a pole carries 1, 2 or 3 cans.
    // He can read the nameplate sometimes, so each value has Not Visible.
    fields: [
      { name: "Transformer Type", list: "Utility Transformer Type", req: true, known: true },
      { name: "Transformers on Pole", list: "Transformers on Pole", req: true, when: isPole, known: true },
      { name: "Utility Name", req: true },
      { name: "Utility Transformer (kVA)", num: true, req: true, when: (r) => !isPole(r) },
      { name: "kVA Each Transformer", num: true, req: true, when: isPole },
      { name: "Primary V", num: true, req: true },
      { name: "Secondary V", num: true, req: true },
      { name: "%Z", num: true, req: true },
      { name: "Winding Connection", list: "Winding Connection", req: true, when: (r) => !isOpenBank(r) },
      { name: "Winding Connection", list: "Winding Connection Open Delta", req: true, when: isOpenBank },
      { name: "Grounding", list: "Grounding", req: true },
      { name: "Available Fault Current (kA)", num: true, office: true },
      { name: "Utility X/R", num: true, office: true },
      { name: "Primary Fuse Amp Rating", num: true },
      { name: "Primary Fuse Type" },
    ],
    opened: false,
  },
  gen: {
    kind: "gen",
    title: "Generator",
    photos: ["Nameplate"],
    optPhotos: [],
    fields: [
      { name: "Rating (kW)", num: true, req: true },
      { name: "Rating (kVA)", num: true, req: true },
      { name: "Voltage", num: true, req: true },
      { name: "Generator X″d", num: true, office: true },
    ],
    opened: false,
  },
  ats: {
    kind: "ats",
    title: "Transfer Switch",
    photos: ["Nameplate", "Front"],
    optPhotos: [],
    fields: [
      { name: "Manufacturer", req: true },
      { name: "Amp Rating", num: true, req: true },
      { name: "Transition", list: "Transition", req: true },
    ],
    opened: false,
  },
  lr: {
    kind: "lr",
    title: "Line Reactor",
    photos: ["Nameplate"],
    optPhotos: [],
    fields: [
      { name: "Inductance (mH)", num: true, req: true },
      { name: "Amps", num: true, req: true },
    ],
    opened: false,
  },
  cap: {
    kind: "cap",
    title: "Capacitor",
    photos: ["Nameplate"],
    optPhotos: [],
    fields: [
      { name: "kVAR", num: true, req: true },
      { name: "Voltage", num: true, req: true },
      { name: "Fixed or Switched", list: "Fixed or Switched", req: true },
    ],
    opened: false,
  },
};

/** A bucket or breaker row: the table's Device tab, then its full fields on the card. */
export function rowFields(isMcc: boolean): Field[] {
  // On a row, only Device Type, Rating and Starter are required (words section 4). The rest is
  // there to read off the photo at the hotel.
  return [
    ...deviceFields("").map((f) => (f.name === "Device Type" ? f : { ...f, req: false })),
    { name: "Rating (A)", num: true, req: true },
    { name: "Setting" },
    ...(isMcc ? [{ name: "Starter Type", list: "Starter Type", req: true } as Field] : []),
  ];
}

// ---- Records ----

export type Source = "Typed" | "Not Visible" | "Typical" | "Measured";

/** One asset's IEA Data record, as Collect reads it. */
export interface Rec {
  f: Record<string, string | number>;
  src: Record<string, Source>;
  photos: Record<string, string>;
  opened: "" | "Opened" | "Not Opened";
  reason: string;
  /** Typed breakers on a panel with no positions in the registry (D31). */
  breakers: Record<string, Rec>;
  /** Required fields tapped into and left (SURVEY_DONE_LOOKED_C1_WORDS.md item 1): looked at, filled or not. */
  looked: Record<string, true>;
}

export const emptyRec = (): Rec => ({ f: {}, src: {}, photos: {}, opened: "", reason: "", breakers: {}, looked: {} });

const STAMP = new Set(["Asset ID", "Site Number", "Recorded By", "Holder Name", "Recorded On", "Recorded At", "Sync Timestamp"]);

/** Reads a stored IEA Data record (cloud or device list). */
export function recFromData(d: Record<string, unknown> | undefined): Rec {
  const r = emptyRec();
  if (!d) return r;
  for (const [k, v] of Object.entries(d)) {
    if (STAMP.has(k) || k === "Field Sources" || k === "Photos" || k === "Opened" || k === "Not Opened Reason" || k === "Breakers" || k === "Looked At") continue;
    if (typeof v === "string" || typeof v === "number") r.f[k] = v;
  }
  const src = d["Field Sources"];
  if (src && typeof src === "object") for (const [k, v] of Object.entries(src as Record<string, unknown>)) r.src[k] = v as Source;
  const ph = d.Photos;
  if (ph && typeof ph === "object") for (const [k, v] of Object.entries(ph as Record<string, unknown>)) if (typeof v === "string" && v) r.photos[k] = v;
  r.opened = d.Opened === "Opened" || d.Opened === "Not Opened" ? d.Opened : "";
  r.reason = typeof d["Not Opened Reason"] === "string" ? d["Not Opened Reason"] : "";
  const lk = d["Looked At"];
  if (lk && typeof lk === "object") for (const [k, v] of Object.entries(lk as Record<string, unknown>)) if (v === true) r.looked[k] = true;
  const br = d.Breakers;
  if (br && typeof br === "object") for (const [k, v] of Object.entries(br as Record<string, unknown>)) r.breakers[k] = recFromData(v as Record<string, unknown>);
  return r;
}

/** Deep merge for stored records: a later save lays over an earlier one, maps included. */
export function mergeData(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const prev = out[k];
    out[k] = v && typeof v === "object" && !Array.isArray(v) && prev && typeof prev === "object" && !Array.isArray(prev) ? mergeData(prev as Record<string, unknown>, v as Record<string, unknown>) : v;
  }
  return out;
}

/** A value is there. Not Visible is gone (SURVEY_DRAWING_IDS_C1_WORDS.md item 7): blank means looked, could not read. */
export function hasValue(r: Rec, f: Field): boolean {
  const v = r.f[f.name];
  return v !== undefined && v !== "";
}

/** Required fields left blank: the amber boxes still showing. They no longer hold the light yellow; the card line counts them. */
export function blanksOf(fields: readonly Field[], r: Rec): number {
  return fields.filter((f) => f.req && !f.office && shown(f, r) && !hasValue(r, f)).length;
}

export function shown(f: Field, r: Rec): boolean {
  return !f.when || f.when(r);
}

/** Four lights (SURVEY_DONE_LOOKED_C1_WORDS.md, approved 2026-10-30 6:41 AM): gray Not Started, yellow Started,
 *  blue Missing Data (every amber field looked at, one or more left empty), green Done (every required field filled). */
export type Status = "none" | "started" | "missing" | "done";

/** Done or Missing Data: the walk counts it finished. Blue never blocks Room Done or a push (item 5). */
export const finished = (s: Status | null | undefined): boolean => s === "done" || s === "missing";

/** A housing, a card or a room from its parts (item 4): any blue with nothing open shows blue. */
export function rollUp(parts: readonly Status[]): Status {
  if (!parts.length || parts.every((x) => x === "none")) return "none";
  if (!parts.every(finished)) return "started";
  return parts.some((x) => x === "missing") ? "missing" : "done";
}

function touched(r: Rec): boolean {
  return Object.keys(r.f).length > 0 || Object.keys(r.photos).length > 0 || Object.keys(r.src).length > 0 || r.opened !== "" || Object.keys(r.looked).length > 0;
}

/** The required fields a status checks: on the card, not office-only, showing now. */
const required = (fields: readonly Field[], r: Rec) => fields.filter((f) => f.req && !f.office && shown(f, r));

/** A required field is looked at once it has a value or was tapped (item 1 and item 8). */
export const lookedAt = (r: Rec, f: Field): boolean => hasValue(r, f) || r.looked[f.name] === true;

/** Fields settle the light once Opened and photos are in: untouched amber is Started, looked-at blanks are Missing Data. */
function byFields(fields: readonly Field[], r: Rec): Status {
  const req = required(fields, r);
  if (req.some((f) => !lookedAt(r, f))) return "started";
  return req.some((f) => !hasValue(r, f)) ? "missing" : "done";
}

/** A card's status: opened (when it opens), every required photo, then its required fields (byFields). */
export function cardStatus(card: Card, r: Rec): Status {
  if (!touched(r)) return "none";
  if (card.opened && r.opened === "Not Opened") return "started";
  for (const p of card.photos) if (!r.photos[p]) return "started";
  if (card.opened && r.opened !== "Opened") return "started";
  return byFields(card.fields, r);
}

/** A row's status. Not Opened always stays on the work list. */
export function rowStatus(r: Rec, isMcc: boolean): Status {
  if (!touched(r)) return "none";
  if (r.opened !== "Opened") return "started";
  if (!r.photos.Row) return "started";
  return byFields(rowFields(isMcc), r);
}

/** A device value on a bucket or breaker proves it was opened (Robert 2026-10-19).
 *  Fills Opened only when it is blank: a Not Opened tap always stands. Not Visible proves nothing. */
export function opensRow(r: Rec, isRow: boolean, value: string): boolean {
  return isRow && r.opened === "" && String(value).trim() !== "";
}

/** What the pilot lights show (Robert 2026-10-16): green when every value the model needs is recorded,
 *  red while one is missing. Needed: Opened, and each required row field (Device Type, Rating, and
 *  Starter Type on an MCC). The photo is not model data. Not Visible is not a value: it stays red. */
export function modelReady(r: Rec, isMcc: boolean): boolean {
  if (r.opened !== "Opened") return false;
  for (const f of rowFields(isMcc)) {
    if (!f.req || !shown(f, r)) continue;
    const v = r.f[f.name];
    if (v === undefined || v === "" || r.src[f.name] === "Not Visible") return false;
  }
  return true;
}

/** Prefills from IEEE 1584 typical values, marked Typical until changed (1584.1 4.3). */
export function prefills(card: Card, a: PackAsset, ieeeClass: string, isWayOut: boolean): Record<string, string | number> {
  if (card.kind !== "housing" && card.kind !== "box") return {};
  const t = typicalFor(codeOf(a), ieeeClass, a.nominalVolts, isWayOut);
  if (!t) return {};
  return { "Electrode Configuration": t.electrode, "Working Distance (in)": t.workingDistanceIn };
}

// ---- Wire (the Map's Connections row, D23) ----

export interface Wire {
  type: "" | "Cable" | "Busway";
  size: string;
  sets: string;
  material: string;
  insulation: string;
  raceway: string;
  length: number | "";
  amps: number | "";
}

export const emptyWire = (): Wire => ({ type: "", size: "", sets: "", material: "", insulation: "", raceway: "", length: "", amps: "" });

export function wireFromRow(d: Record<string, unknown> | undefined): Wire {
  if (!d) return emptyWire();
  const s = (k: string) => (typeof d[k] === "string" ? (d[k] as string) : "");
  const n = (k: string): number | "" => (typeof d[k] === "number" ? (d[k] as number) : "");
  const t = s("Link Type");
  return {
    type: t === "Cable" || t === "Busway" ? t : "",
    size: s("Conductor Size"),
    sets: s("Conductors per Phase"),
    material: s("Conductor Material"),
    insulation: s("Insulation Type"),
    raceway: s("Raceway Type"),
    length: n("Est. Length (ft)"),
    amps: n("Busway Amp Rating"),
  };
}

/** Only the wire fields. Collect never writes the link fields. */
export function wireRow(w: Wire): Record<string, unknown> {
  return {
    "Link Type": w.type,
    "Conductor Size": w.size,
    "Conductors per Phase": w.sets,
    "Conductor Material": w.material,
    "Insulation Type": w.insulation,
    "Raceway Type": w.raceway,
    "Est. Length (ft)": w.length,
    "Busway Amp Rating": w.amps,
  };
}

export function wireStatus(w: Wire): Status {
  const need = w.type === "Busway" ? [w.amps, w.material, w.length] : [w.size, w.sets, w.material, w.raceway, w.length];
  const have = need.filter((x) => x !== "").length;
  const any = have > 0 || w.type !== "" || w.insulation !== "";
  return have === need.length && (w.type !== "" || w.size !== "") ? "done" : any ? "started" : "none";
}

/** "#2" up to 4/0, "250 kcmil" from 250 up. */
export function sizeWords(z: string): string {
  if (!z) return "?";
  return /^\d{3}$/.test(z) ? `${z} kcmil` : `#${z}`;
}

/** The wire tag on the diagram: "Wire" when empty, "#2/0 · 2 sets · 85 ft" when filled. */
export function wireTag(w: Wire): string {
  if (wireStatus(w) === "none") return "Wire";
  if (w.type === "Busway") return `Busway · ${w.amps || "?"} A · ${w.length || "?"} ft`;
  return `${sizeWords(w.size)} · ${w.sets || "?"} set${w.sets === "1" ? "" : "s"} · ${w.length || "?"} ft`;
}

/** Quick Pick's line for a wire: everything but the length. */
export function wireSig(w: Wire): string {
  if (w.type === "Busway") return `Busway · ${w.amps} A · ${w.material}`;
  return `${sizeWords(w.size)} · ${w.sets} set${w.sets === "1" ? "" : "s"} · ${w.material === "Aluminum" ? "Al" : w.material === "Copper" ? "Cu" : w.material} · ${w.insulation || "?"} · ${w.raceway || "?"}`;
}

/** Quick Pick: the site's finished wires, one line each, newest first. Never the length. */
export function quickPicks(wires: readonly Wire[]): Wire[] {
  const seen = new Set<string>();
  const out: Wire[] = [];
  for (const w of [...wires].reverse()) {
    if (wireStatus(w) !== "done") continue;
    const k = wireSig(w);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ ...w, length: "" });
  }
  return out;
}

const STEEL = new Set(["EMT", "Rigid Steel (RMC/IMC)", "Flex (FMC/LFMC)", "Steel Conduit"]);
/** The model needs only steel or non-steel. Figured from the pick. */
export function isSteel(raceway: string): boolean | null {
  if (!raceway || raceway === "Other") return null;
  return STEEL.has(raceway);
}

// ---- The site: what Collect shows on the diagram ----

export interface Site {
  reg: Registry;
  tree: MapTree;
  links: readonly MapLink[];
  recs: Record<string, Rec>;
  wires: Record<string, Wire>;
  ieeeClassOf: Record<string, string>;
}

export function recOf(site: Pick<Site, "recs">, assetId: string): Rec {
  return site.recs[assetId] ?? emptyRec();
}

/** The link that draws the line into a node, when it carries a wire (assets and loads). */
export function wireLinkOf(n: MapNode): string | null {
  return (n.kind === "asset" || n.kind === "load") && n.linkId ? n.linkId : null;
}

export function wireOf(site: Pick<Site, "wires">, linkId: string): Wire {
  return site.wires[linkId] ?? emptyWire();
}

/** Required unless the node is a load (Robert, 2026-10-16: wire on loads is optional). */
export const wireRequired = (n: MapNode) => n.kind === "asset";

/** A housing's rows: its registry positions plus typed breakers from the map, in order. */
export function rowsOf(site: Pick<Site, "reg" | "tree"> & Partial<Pick<Site, "recs">>, assetId: string): { point: string; assetId: string | null }[] {
  const reg = site.reg.positionsOf(assetId).map((p) => ({ point: p, assetId: `${assetId}-${p}` }));
  const typed: { point: string; assetId: string | null }[] = [];
  for (const n of site.tree.nodes.values()) {
    if (n.kind === "point" && n.asset === assetId && !reg.some((r) => r.point === n.ref) && n.ref !== "Lugs") typed.push({ point: n.ref, assetId: null });
  }
  // Breakers tapped in on the panel circles live only in the housing record (Gear View words 9).
  const br = site.recs?.[assetId]?.breakers ?? {};
  for (const [k, r] of Object.entries(br)) if (r.f.Slot === "Breaker" && !reg.some((x) => x.point === k) && !typed.some((x) => x.point === k)) typed.push({ point: k, assetId: null });
  const num = (p: string) => (/^\d+$/.test(p) ? Number(p) : NaN);
  typed.sort((a, b) => (Number.isNaN(num(a.point)) || Number.isNaN(num(b.point)) ? 0 : num(a.point) - num(b.point)));
  // A Space or a Removed circle is not a breaker, so it is not a row.
  return [...reg, ...typed].filter((row) => row.assetId !== null || !["Space", "Removed"].includes(String(br[row.point]?.f.Slot ?? "")));
}

/** Openings marked Space on a panel's circles: no breaker installed. */
export function spacesOf(site: Pick<Site, "recs">, assetId: string): string[] {
  return Object.entries(site.recs[assetId]?.breakers ?? {})
    .filter(([, r]) => r.f.Slot === "Space")
    .map(([k]) => k);
}

/** The panel's size from its record, or null when not set. */
export function circuitsOf(site: Pick<Site, "recs">, assetId: string): number | null {
  const v = Number(site.recs[assetId]?.f.Circuits);
  return Number.isInteger(v) && v > 0 ? v : null;
}

export function rowRec(site: Pick<Site, "recs">, housing: string, row: { point: string; assetId: string | null }): Rec {
  if (row.assetId) return recOf(site, row.assetId);
  return recOf(site, housing).breakers[row.point] ?? emptyRec();
}

export function isMccAsset(site: Pick<Site, "reg">, assetId: string): boolean {
  const a = site.reg.byId.get(assetId);
  return Boolean(a) && codeOf(a!) === "MCC";
}

export function cardFor(site: Pick<Site, "reg"> & Partial<Pick<Site, "recs">>, assetId: string): Card {
  const card = CARDS[cardKindOf(site.reg.byId.get(assetId) ?? { assetId, equipmentCode: "", equipmentType: "" })];
  // A pole mount's nameplates are out of reach: the photo is the pole and its cans.
  if (card.kind === "utl" && site.recs && isPole(recOf(site as Pick<Site, "recs">, assetId))) return POLE_CARD;
  return card;
}

/** The utility card when the transformer is on a pole. */
const POLE_CARD: Card = { ...CARDS.utl, photos: ["Pole and Transformers"], optPhotos: ["Meter", "Service Entrance", "Nameplate"] };

/** A node's ring. A housing is done only when its card and every row are done. */
export function nodeStatus(site: Site, n: MapNode): Status | null {
  if (n.kind === "room") return null;
  if (n.kind === "load") return null;
  if (n.kind === "point") {
    const row = rowsOf(site, n.asset).find((r) => r.point === n.ref);
    return row ? rowStatus(rowRec(site, n.asset, row), isMccAsset(site, n.asset)) : null;
  }
  const card = cardFor(site, n.ref);
  const s = cardStatus(card, recOf(site, n.ref));
  if (card.kind !== "housing") return s;
  const rows = rowsOf(site, n.ref).map((r) => rowStatus(rowRec(site, n.ref, r), isMccAsset(site, n.ref)));
  return rollUp([s, ...rows]);
}

export function rowsLeft(site: Site, assetId: string): number {
  return rowsOf(site, assetId).filter((r) => !finished(rowStatus(rowRec(site, assetId, r), isMccAsset(site, assetId)))).length;
}

/** What is left below a node, for the folded pill: nodes not done plus required wires not done. */
export function leftBelow(site: Site, key: string): number {
  let c = 0;
  for (const k of site.tree.kids.get(key) ?? []) {
    const n = site.tree.nodes.get(k)!;
    const s = nodeStatus(site, n);
    if ((n.kind === "asset" || n.kind === "source") && !finished(s)) c++;
    const lid = wireLinkOf(n);
    if (lid && wireRequired(n) && wireStatus(wireOf(site, lid)) !== "done") c++;
    c += leftBelow(site, k);
  }
  return c;
}

export interface Totals {
  nodes: number;
  wires: number;
  photos: number;
}

/** The strip's site total: "Left: 42 nodes · 18 wires · 63 photos". Only what is on the map. */
export function totals(site: Site): Totals {
  let nodes = 0;
  let wires = 0;
  let photos = 0;
  for (const n of site.tree.nodes.values()) {
    if (n.kind === "asset" || n.kind === "source") {
      const card = cardFor(site, n.ref);
      const r = recOf(site, n.ref);
      if (!finished(cardStatus(card, r))) nodes++;
      for (const p of card.photos) if (!r.photos[p]) photos++;
      if (card.kind === "housing") {
        for (const row of rowsOf(site, n.ref)) {
          const rr = rowRec(site, n.ref, row);
          if (!finished(rowStatus(rr, isMccAsset(site, n.ref)))) nodes++;
          if (!rr.photos.Row && rr.opened !== "Not Opened") photos++;
        }
      }
    }
    const lid = wireLinkOf(n);
    if (lid && wireRequired(n) && wireStatus(wireOf(site, lid)) !== "done") wires++;
  }
  return { nodes, wires, photos };
}

// ---- The work list (figured, never stored) ----

export interface WorkLine {
  key: string;
  label: string;
  status: Status;
  rowsLeft: number;
  wireLeft: boolean;
  notOpened: number;
}

export interface WorkRoom {
  room: string;
  lines: WorkLine[];
}

/** By room, in the order the chains run. Every mapped asset with what is left on it. */
export function workList(site: Site): { rooms: WorkRoom[]; totals: Totals } {
  const by = new Map<string, WorkLine[]>();
  const visit = (key: string, room: string) => {
    const n = site.tree.nodes.get(key)!;
    let here = room;
    if (n.kind === "room") here = n.ref;
    if (n.kind === "asset" || n.kind === "source") {
      const a = site.reg.byId.get(n.ref);
      const place = n.kind === "source" ? "Outside" : a ? (placeOf(a).room ?? here) : here;
      const housing = cardFor(site, n.ref).kind === "housing";
      const lid = wireLinkOf(n);
      const line: WorkLine = {
        key,
        label: a ? shortName(a) : n.ref,
        status: nodeStatus(site, n) ?? "none",
        rowsLeft: housing ? rowsLeft(site, n.ref) : 0,
        wireLeft: Boolean(lid && wireRequired(n) && wireStatus(wireOf(site, lid)) !== "done"),
        notOpened: housing ? rowsOf(site, n.ref).filter((r) => rowRec(site, n.ref, r).opened === "Not Opened").length : 0,
      };
      by.set(place, [...(by.get(place) ?? []), line]);
    }
    for (const k of site.tree.kids.get(key) ?? []) visit(k, here);
  };
  for (const r of site.tree.roots) visit(r, "Outside");
  return { rooms: [...by.entries()].map(([room, lines]) => ({ room, lines })), totals: totals(site) };
}

// ---- Saving ----

export interface Change {
  /** The record it lands in: the asset's own, or the housing's for a typed breaker. */
  assetId: string;
  /** A typed breaker's number, when the change belongs inside the housing record. */
  breaker?: string;
  data: Record<string, unknown>;
}

/** The stored shape of one field change. Typical prefills turn Measured when changed. */
export function fieldChange(f: Field, value: string, prefilled: string | number | undefined): Record<string, unknown> {
  const v: string | number = f.num && value.trim() !== "" && !Number.isNaN(Number(value)) ? Number(value) : value.trim();
  const src: Source = prefilled !== undefined && v !== "" ? (String(prefilled) === String(v) ? "Typical" : "Measured") : "Typed";
  return { [f.name]: v, "Field Sources": { [f.name]: v === "" ? "Typed" : src } };
}

export function notVisibleChange(f: Field, on: boolean): Record<string, unknown> {
  return on ? { [f.name]: "", "Field Sources": { [f.name]: "Not Visible" } } : { "Field Sources": { [f.name]: "Typed" } };
}

/** Wraps a typed breaker's change inside the housing record (Breakers.<number>). */
export function placeChange(c: Change): Record<string, unknown> {
  return c.breaker ? { Breakers: { [c.breaker]: c.data } } : c.data;
}

/** The record's path. One per asset, ID = Asset ID (rule 4c). */
export const ieaPath = (company: string, site: string, assetId: string) => `companies/${company}/sites/${site}/ieaData/${assetId}`;

/** Side feeds into a node that carry a wire too: a second source, a bucket fed separately. */
export function sideFeedsInto(site: Pick<Site, "links">, assetId: string, point?: string): MapLink[] {
  return site.links.filter((l) => l.status !== "Removed" && l.goesTo === "Equipment" && l.toAsset === assetId && (point ? l.toPoint === point && isPositionPoint(point) : l.toPoint === "Emergency"));
}

// ---- Photos mode (IEA_PHOTO_VIEWER_WORDS.md, Robert 2026-10-16) ----

/** One photo place on an asset: taken (path set) or not. Target is where Take It Now saves. */
export interface PhotoSlot {
  type: string;
  path: string;
  required: boolean;
  target: string;
}

const tgt = (assetId: string, breaker?: string) => (breaker ? `B:${assetId}:${breaker}` : `A:${assetId}`);

/** An asset's own photos: the card's required ones, then optional and any other type it holds. */
export function photoSlots(site: Pick<Site, "reg" | "recs">, assetId: string): PhotoSlot[] {
  const card = cardFor(site, assetId);
  const r = recOf(site, assetId);
  const out: PhotoSlot[] = card.photos.map((p) => ({ type: p, path: r.photos[p] ?? "", required: true, target: tgt(assetId) }));
  for (const p of card.optPhotos) if (r.photos[p]) out.push({ type: p, path: r.photos[p]!, required: false, target: tgt(assetId) });
  for (const [p, path] of Object.entries(r.photos)) if (!out.some((x) => x.type === p)) out.push({ type: p, path, required: false, target: tgt(assetId) });
  return out;
}

/** Bucket and breaker photos (Robert 2026-10-20): up to 4. Photo 1 is the outside, stored as "Row" so
 *  photos already taken carry over. It is required and is the asset's picture. 2 to 4 are optional, unlabeled. */
export const ROW_EXTRAS = ["Photo 2", "Photo 3", "Photo 4"] as const;
export const ROW_MAX = 4;

/** What photo 1 is called: the Door on an MCC bucket or switchboard feeder, the Breaker on a panel. */
export function rowPhotoWord(panel: boolean): string {
  return panel ? "Breaker" : "Door";
}

/** The photos a bucket or breaker has, photo 1 first. */
export function rowPhotoKeys(r: Rec): string[] {
  return ["Row", ...ROW_EXTRAS].filter((k) => r.photos[k]);
}

/** The next optional photo to offer: only once photo 1 is in, and never past 4. */
export function nextRowExtra(r: Rec): string | null {
  if (!r.photos.Row) return null;
  return ROW_EXTRAS.find((k) => !r.photos[k]) ?? null;
}

/** A housing row's photo. Not Opened rows have none and need none. */
export function rowPhoto(site: Pick<Site, "recs">, housing: string, row: { point: string; assetId: string | null }): PhotoSlot & { notOpened: boolean } {
  const r = rowRec(site, housing, row);
  return { type: "Row", path: r.photos.Row ?? "", required: r.opened !== "Not Opened", target: row.assetId ? tgt(row.assetId) : tgt(housing, row.point), notOpened: r.opened === "Not Opened" };
}

/** The number on a node in Photos mode: photos taken. A housing counts its rows too. Rooms and loads: none. */
export function photoCount(site: Pick<Site, "reg" | "recs" | "tree">, n: MapNode): number | null {
  if (n.kind === "room" || n.kind === "load") return null;
  if (n.kind === "point") {
    const row = rowsOf(site, n.asset).find((r) => r.point === n.ref) ?? { point: n.ref, assetId: null };
    return Object.keys(rowRec(site, n.asset, row).photos).length;
  }
  let c = Object.keys(recOf(site, n.ref).photos).length;
  if (cardFor(site, n.ref).kind === "housing") for (const row of rowsOf(site, n.ref)) c += Object.keys(rowRec(site, n.ref, row).photos).length;
  return c;
}

/** Every photo on the map, each counted once (rows through their housing). */
export function photoTotal(site: Pick<Site, "reg" | "recs" | "tree">): number {
  let c = 0;
  for (const n of site.tree.nodes.values()) if (n.kind === "asset" || n.kind === "source") c += photoCount(site, n) ?? 0;
  return c;
}

/** "00-ER1-SWBD-01_20261016_nameplate.jpg" gives "10/16/2026". */
export function photoDate(path: string): string {
  const m = /_(\d{4})(\d{2})(\d{2})_/.exec(path.split("/").pop() ?? "");
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}

// ---- Photo flow (IEA_PHOTO_FLOW_WORDS.md, Robert 2026-10-16) ----

/** Field order for Take Photos: doors closed first, the close-up last. */
export const FIELD_ORDER = ["Front", "Exterior", "Old Label", "Pad Mount Plate", "Nameplate"];

/** What Take Photos walks through: the card's photos still needed, in field order. */
export function guidedTypes(card: Pick<Card, "photos">, r: Rec): string[] {
  const rank = (t: string) => {
    const i = FIELD_ORDER.indexOf(t);
    return i < 0 ? FIELD_ORDER.length : i;
  };
  return card.photos.filter((t) => !r.photos[t]).sort((a, b) => rank(a) - rank(b));
}

/** The labels a photo can be changed to on its big view: the card's own photo types. */
export function labelChoices(card: Pick<Card, "photos" | "optPhotos" | "kind">): string[] {
  const all = [...card.photos, ...card.optPhotos];
  // A utility photo can move between the pad mount and pole mount labels.
  if (card.kind === "utl") for (const p of [...CARDS.utl.photos, ...POLE_CARD.photos]) if (!all.includes(p)) all.push(p);
  return all;
}

/** One line under Deleted Photos on the record. The cloud copy stays out of sight (Robert's call). */
export function deletedEntry(path: string, type: string, why: "Deleted" | "Retaken", who: { badgeId: string; holderName: string }, at: string): Record<string, string> {
  return { Path: path, Type: type, Why: why, "Badge ID": who.badgeId, "Holder Name": who.holderName, At: at };
}

/** A key for Deleted Photos that sorts by time and never holds a dot. */
export const deletedKey = (at: string) => at.replace(/[^0-9]/g, "").slice(0, 17);

/** Change a photo's label. If the new label already has a photo, the two swap. */
export function relabelChange(r: Rec, from: string, to: string): Record<string, unknown> | null {
  const a = r.photos[from];
  if (!a || from === to) return null;
  return { Photos: { [to]: a, [from]: r.photos[to] ?? "" } };
}

/**
 * Undo on Same As Above (CANCEL_WORDS.md section 3): the copied fields back as they were, each with its source.
 * A field that was blank goes back to blank, the way a cleared field is saved.
 */
export function sameAboveUndo(before: Pick<Rec, "f" | "src" | "opened">, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = { Opened: before.opened };
  const sources: Record<string, string> = {};
  for (const k of keys) {
    out[k] = before.f[k] ?? "";
    sources[k] = before.src[k] ?? "Typed";
  }
  out["Field Sources"] = sources;
  return out;
}
